import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  listAppointmentsEnabledRequests,
  approveAppointmentsEnabledRequest,
  rejectAppointmentsEnabledRequest,
} from "@/lib/api/admin";
import type { Store } from "@/lib/api/types";
import { DataTable, type Column } from "@/components/DataTable";
import { Pagination } from "@/components/Pagination";
import { PAGE_SIZE } from "@/lib/constants";
import { formatDateTime } from "@/lib/utils/date";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CheckCircle, XCircle } from "lucide-react";

type Action = { type: "approve" | "reject"; store: Store };

export function AppointmentsEnabledRequestsPage() {
  const user = useAdminAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [offset, setOffset] = useState(0);
  const [action, setAction] = useState<Action | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["appointments-enabled-requests", offset],
    queryFn: async () => listAppointmentsEnabledRequests(user!, { limit: PAGE_SIZE, offset }),
    enabled: !!user,
  });

  const approveMutation = useMutation({
    mutationFn: async (storeId: string) => approveAppointmentsEnabledRequest(user!, storeId),
    onSuccess: (_, storeId) => {
      void qc.invalidateQueries({ queryKey: ["appointments-enabled-requests"] });
      void qc.invalidateQueries({ queryKey: ["store", storeId] });
      setAction(null);
      toast.success("Appointment booking approved.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const rejectMutation = useMutation({
    mutationFn: async (storeId: string) => rejectAppointmentsEnabledRequest(user!, storeId),
    onSuccess: (_, storeId) => {
      void qc.invalidateQueries({ queryKey: ["appointments-enabled-requests"] });
      void qc.invalidateQueries({ queryKey: ["store", storeId] });
      setAction(null);
      toast.success("Appointment booking request rejected.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const isPending = approveMutation.isPending || rejectMutation.isPending;

  const columns: Column<Store>[] = [
    {
      key: "name",
      header: "Store",
      render: (s) => (
        <button
          className="font-medium text-sm hover:underline text-left"
          onClick={(e) => {
            e.stopPropagation();
            void navigate(`/stores/${s.id}`);
          }}
        >
          {s.name}
        </button>
      ),
    },
    {
      key: "category",
      header: "Category",
      render: (s) => <span className="text-sm capitalize">{s.category}</span>,
    },
    {
      key: "requested",
      header: "Requested At",
      render: (s) => (
        <span className="text-sm text-muted-foreground">
          {s.appointments_enabled_requested_at
            ? formatDateTime(s.appointments_enabled_requested_at)
            : "—"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (s) => (
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setAction({ type: "approve", store: s });
            }}
          >
            <CheckCircle className="size-3.5 mr-1 text-green-600" /> Approve
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setAction({ type: "reject", store: s });
            }}
          >
            <XCircle className="size-3.5 mr-1 text-destructive" /> Reject
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Appointment Booking Requests</h1>
        <p className="text-sm text-muted-foreground">
          Stores requesting to enable appointment booking
        </p>
      </div>

      <DataTable
        columns={columns}
        data={data?.data ?? []}
        isLoading={isLoading}
        rowKey={(s) => s.id}
        emptyMessage="No appointment booking requests."
      />

      {data && (
        <Pagination
          offset={offset}
          limit={PAGE_SIZE}
          total={data.total}
          onOffsetChange={setOffset}
        />
      )}

      <ConfirmDialog
        open={action !== null}
        onOpenChange={(open) => !open && setAction(null)}
        title={
          action?.type === "approve" ? "Approve appointment booking" : "Reject appointment booking"
        }
        description={
          action?.type === "approve"
            ? `Approve appointment booking for "${action?.store.name}"? They'll be able to configure staff, services, and accept bookings.`
            : `Reject the appointment booking request for "${action?.store.name}".`
        }
        confirmLabel={action?.type === "approve" ? "Approve" : "Reject"}
        destructive={action?.type === "reject"}
        isPending={isPending}
        onConfirm={() => {
          if (!action) return;
          if (action.type === "approve") {
            approveMutation.mutate(action.store.id);
          } else {
            rejectMutation.mutate(action.store.id);
          }
        }}
      />
    </div>
  );
}
